<template>
  <div class="spot-statement">
    <!-- 工具栏：日期区间 + 颗粒度 + 电表 + 打印 -->
    <div class="toolbar">
      <el-form :inline="true" class="toolbar-form">
        <el-form-item label="日期">
          <el-date-picker
            v-model="timeRange"
            type="daterange"
            :clearable="false"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="YYYY-MM-DD"
            :disabled-date="disabledDate"
            @change="handleQuery"
          />
        </el-form-item>
        <el-form-item label="颗粒度">
          <el-radio-group v-model="granularity">
            <el-radio-button v-for="g in SPOT_GRANULARITIES" :key="g.value" :label="g.value">
              {{ g.label }}
            </el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="电表" v-if="meterList.length">
          <el-select v-model="selectedMeter" @change="handleQuery" style="width: 180px">
            <el-option label="全部" value="all" />
            <el-option v-for="m in meterList" :key="m.uuid" :label="m.meterName" :value="m.uuid" />
          </el-select>
        </el-form-item>
      </el-form>
      <div class="toolbar-actions">
        <el-button type="primary" plain :disabled="!rows.length" @click="handleExport">导出明细</el-button>
      </div>
    </div>

    <!-- 汇总卡片 -->
    <div class="summary-cards" v-loading="loading">
      <div class="summary-card" v-for="card in summaryCards" :key="card.label">
        <div class="card-label">{{ card.label }}</div>
        <div class="card-value">
          <span :class="{ 'is-negative': card.negative, 'is-blank': card.blank }">{{ card.value }}</span>
          <span class="card-unit">{{ card.unit }}</span>
        </div>
      </div>
    </div>

    <!-- 明细表：与收益总览共用同一个组件 -->
    <div class="detail-wrapper">
      <SpotDetailTablePanel
        :rows="rows"
        :granularity="granularity"
        :loading="loading"
        :summary="summary"
        :meterMap="meterMap"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import dayjs from 'dayjs'
import { ElMessage } from 'element-plus'
import useAppStore from '@/store/modules/app'
import { fmtIncome } from '@/utils/fortmatter.js'
import { useSpotSettlement, SPOT_GRANULARITIES } from '../../composables/useSpotSettlement.js'
import { exportSpotDetail } from '../../composables/exportSpotDetail.js'
import SpotDetailTablePanel from '../over-view/spot/SpotDetailTablePanel/index.vue'
import { queryStationMeterList } from '../over-view/api/index.js'

defineOptions({ name: 'SpotStatement' })

const appStore = useAppStore()

// 结算单场景默认按天，逐格明细可手动切到 15min
const spot = useSpotSettlement({ defaultGranularity: 'day' })
const { granularity, rows, loading, summary, maxSpanDays } = spot

// 现货价格 T+1 才结算完整，默认只看前一天。
// 组件由 Statement.vue 的 v-if="isSpot" 控制，每次切到现货口径都会重新挂载，
// 所以这里的初值即「每次切换时自动回到前一天」。
const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD')
const timeRange = ref([yesterday, yesterday])

const meterList = ref([])
const selectedMeter = ref('all')

const meterMap = computed(() => {
  const map = {}
  meterList.value.forEach((it) => {
    if (it?.uuid) map[it.uuid] = it.meterName
  })
  return map
})

function disabledDate(date) {
  // 与抄表结算保持一致：不能选未来，且最多回溯半年
  return dayjs(date) > dayjs() || dayjs(date) < dayjs().subtract(6, 'month').startOf('month')
}

function blankOr(value, frac = 2) {
  if (value === null || value === undefined) return '--'
  return fmtIncome(value, frac)
}

const summaryCards = computed(() => {
  const s = summary.value || {}
  const isBlankVal = (v) => v === null || v === undefined
  return [
    { label: '总充电量', value: blankOr(s.chargeEnergy), unit: 'kWh', blank: isBlankVal(s.chargeEnergy) },
    { label: '总放电量', value: blankOr(s.dischargeEnergy), unit: 'kWh', blank: isBlankVal(s.dischargeEnergy) },
    {
      label: '实时价总收益', value: blankOr(s.realtimeIncome), unit: '元',
      blank: isBlankVal(s.realtimeIncome), negative: !isBlankVal(s.realtimeIncome) && s.realtimeIncome < 0,
    },
    {
      label: '日前价总收益', value: blankOr(s.dayaheadIncome), unit: '元',
      blank: isBlankVal(s.dayaheadIncome), negative: !isBlankVal(s.dayaheadIncome) && s.dayaheadIncome < 0,
    },
    {
      label: '实时较日前', value: blankOr(s.incomeDiff), unit: '元',
      blank: isBlankVal(s.incomeDiff), negative: !isBlankVal(s.incomeDiff) && s.incomeDiff < 0,
    },
    {
      label: '实时价覆盖率',
      value: isBlankVal(s.rtCoverage) ? '--' : (s.rtCoverage * 100).toFixed(1),
      unit: '%',
      blank: isBlankVal(s.rtCoverage),
    },
  ]
})

async function handleQuery() {
  const [start, end] = timeRange.value || []
  if (!start || !end) return
  const check = spot.validateSpan(start, end)
  if (!check.ok) {
    ElMessage.warning(check.message)
    return
  }
  await spot.fetchSpotStat({ startDate: start, endDate: end, uuid: selectedMeter.value })
}

/** 切颗粒度时若区间超限，先裁到合法范围再查，而不是甩个警告 */
function clampAndQuery() {
  const limit = maxSpanDays.value
  const [start, end] = timeRange.value || []
  if (limit != null && start && end) {
    const spanDays = dayjs(end).diff(dayjs(start), 'day') + 1
    if (spanDays > limit) {
      timeRange.value = [dayjs(end).subtract(limit - 1, 'day').format('YYYY-MM-DD'), end]
    }
  }
  handleQuery()
}

watch(granularity, clampAndQuery)

watch(() => appStore.stationId, (id) => {
  if (id) {
    loadMeterList()
    handleQuery()
  }
})

async function loadMeterList() {
  if (!appStore.stationId) return
  try {
    const res = await queryStationMeterList({ stationId: appStore.stationId })
    const list = Array.isArray(res?.rows) ? res.rows : []
    // 与收益总览一致，只取并网表
    meterList.value = list.filter((it) => Number(it.meterType) === 3)
  } catch (e) {
    console.error('获取电表列表失败:', e)
    meterList.value = []
  }
}

/** 导出当前区间的全量明细为 CSV，而不是打印页面截图 */
function handleExport() {
  const [start, end] = timeRange.value || []
  const ok = exportSpotDetail({
    rows: rows.value,
    granularity: granularity.value,
    meterMap: meterMap.value,
    stationName: appStore.stationName,
    startDate: start,
    endDate: end,
  })
  if (!ok) ElMessage.warning('当前没有可导出的明细数据')
}

onMounted(() => {
  loadMeterList()
  handleQuery()
})
</script>

<style lang="scss" scoped>
.spot-statement {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 12px;

    .toolbar-form {
      display: flex;
      align-items: center;
      flex-wrap: wrap;

      :deep(.el-form-item) {
        margin-bottom: 0;
        margin-right: 16px;
      }
    }
  }

  .summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;

    .summary-card {
      background: #f7f9fc;
      border-radius: 8px;
      padding: 14px 16px;

      .card-label {
        color: #999;
        font-family: 'HarmonyOS Sans SC';
        font-size: 12px;
        margin-bottom: 6px;
      }

      .card-value {
        color: #2e2e2e;
        font-family: 'HarmonyOS Sans SC';
        font-size: 20px;
        font-weight: 500;

        .card-unit {
          color: #999;
          font-size: 12px;
          margin-left: 4px;
        }

        .is-negative {
          color: #f56c6c;
        }

        .is-blank {
          color: #bbb;
        }
      }
    }
  }

  .detail-wrapper {
    min-height: 460px;
  }
}
</style>
